import { useEffect, useRef, useState } from "react";

import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";

import {
  BwImageAttachment,
  BwZoomInLine,
  BwZoomOutLine,
} from "@/components/ui/icons";
import type { LightBoxAttachment } from "@/components/ui/lightbox/light-box-attachment";
import LightBoxItem from "@/components/ui/lightbox/light-box-item";
import { useScreen } from "@/hooks/use-screen";

type LightBoxItemImageProps<
  TAttachment extends LightBoxAttachment = LightBoxAttachment,
> = {
  item: TAttachment;
};

const LightBoxItemImage = (props: LightBoxItemImageProps) => {
  const { item } = props;
  const ref = useRef<any>(null);
  const imgRef = useRef<any>(null);
  const zoomRef = useRef<any>(null);
  const sizeImg = useScreen({
    base: "large",
    lg: "xlarge",
    md: "large",
    sm: "medium",
    xs: "medium",
  });
  const [area, setArea] = useState({
    x: 0,
    y: 0,
    scale: 1,
    value: "scale3d(1,1, 1) translate3d(0px, 0px, 0)",
  });

  const onUpdate = ({ x, y, scale }: any) => {
    const { current: img } = imgRef;
    if (img) {
      const value = make3dTransformValue({ x, y, scale });
      setArea({ x: x * -1, y: y * -1, scale, value });
    }
  };

  const onZoomIn = () => {
    if (imgRef.current) {
      const data = {
        x: imgRef.current.offsetWidth / 2,
        y: imgRef.current.offsetHeight / 2,
        scale: area.scale + 1,
      };
      zoomRef.current.alignCenter(data);
    }
  };

  const onZoomOut = () => {
    if (imgRef.current) {
      const data = {
        x: imgRef.current.offsetWidth / 2,
        y: imgRef.current.offsetHeight / 2,
        scale: area.scale - 1,
      };
      zoomRef.current.alignCenter(data);
    }
  };
  // A blob: URL is already the fully-resolved image bytes (birdie-chat mints these
  // from the authenticated /attachments fetch); it carries no query string, so
  // appending ?variant= yields an unregistered URL and the <img> fails to load.
  // Only media-service HTTP URLs support variant sizing, and not for every file:
  // one the media service never sized answers 400, so the file itself comes next.
  const sources = item.contentUri?.startsWith("blob:")
    ? [item.contentUri]
    : [`${item.contentUri}?variant=${sizeImg}`, item.contentUri ?? ""];
  const [at, setAt] = useState(0);
  const sizedSrc = sources[0];
  useEffect(() => setAt(0), [sizedSrc]);
  const imageSrc = sources[at];
  // No address the browser can decode — a HEIC whose webp rewrite the media
  // service has not written yet, in a browser that draws no HEIC. Say so instead
  // of showing the broken glyph.
  const failed = imageSrc === undefined;

  if (failed) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-screen gap-2 text-text-light">
        <BwImageAttachment className="size-16" />
        <p className="text-sm font-medium">{item.fileName}</p>
        <p className="text-xs text-text-light/72">
          The preview is still being prepared. Check back in a moment.
        </p>
      </div>
    );
  }

  return (
    <div>
      <QuickPinchZoom
        ref={zoomRef}
        onUpdate={onUpdate}
        animationDuration={0}
        wheelScaleFactor={1000}
        maxZoom={50}
        minZoom={1}
        shouldInterceptWheel={(e) => {
          return e.ctrlKey || e.metaKey;
        }}
        containerProps={{
          className: "h-screen w-screen",
        }}
      >
        <div ref={imgRef} style={{ transform: area.value }}>
          <LightBoxItem item={item}>
            <img
              ref={ref}
              className="max-w-[calc(100%_-_48px)] mx-auto max-h-[calc(100vh-120px)] select-none"
              src={imageSrc}
              alt={item.fileName ?? ""}
              onError={() => setAt((current) => current + 1)}
            />
          </LightBoxItem>
        </div>
      </QuickPinchZoom>
      <div className="absolute flex items-center justify-center w-full gap-2 bottom-6">
        <div
          className="backdrop-blur-md rounded-full w-10 h-10 cursor-pointer flex items-center justify-center border-white/[0.24] bg-bg-dark-max/[0.72]"
          onClick={onZoomOut}
        >
          <BwZoomOutLine className="w-5 h-5 text-text-light" />
        </div>
        <div
          className="backdrop-blur-md rounded-full w-10 h-10 cursor-pointer flex items-center justify-center border-white/[0.24] bg-bg-dark-max/[0.72]"
          onClick={onZoomIn}
        >
          <BwZoomInLine className="w-5 h-5 text-text-light" />
        </div>
      </div>
    </div>
  );
};

export default LightBoxItemImage;
